import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/stores/auth.store';
import { useTheme } from '@/stores/theme.store';
import { useWebSocket } from '@/lib/ws/useWebSocket';

import { TooltipProvider } from '@/components/ui/tooltip';
import { CommandPalette } from '@/components/shortcuts/CommandPalette';
import { Composer } from '@/components/compose/Composer';

import { LoginPage } from '@/pages/Login';
import { RegisterPage } from '@/pages/Register';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { SetupPage } from '@/pages/Setup';
import { InboxPage } from '@/pages/Inbox';
import { DraftsPage } from '@/pages/Drafts';
import { SettingsPage } from '@/pages/Settings';
import { ContactsPage } from '@/pages/Contacts';
import { DocumentsPage } from '@/pages/Documents';
import { RulesPage } from '@/pages/Rules';
import { InboxZeroPage } from '@/pages/InboxZero';
import { CalendarPage } from '@/pages/Calendar';
import { AiRepliesPage } from '@/pages/AiReplies';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  const location = useLocation();
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AuthenticatedShell() {
  // Hook attivato solo dopo login
  useWebSocket();
  return (
    <>
      <CommandPalette />
      <AnimatePresence>
        <Composer />
      </AnimatePresence>
    </>
  );
}

export function App() {
  const loadCurrentUser = useAuth((s) => s.loadCurrentUser);
  const applyTheme = useTheme((s) => s.applyTheme);
  const user = useAuth((s) => s.user);

  useEffect(() => {
    void loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    applyTheme();
    // Riapplica se cambia preferenza sistema
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => applyTheme();
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, [applyTheme]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <BrowserRouter>
          {user && <AuthenticatedShell />}
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
            <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
            <Route path="/rules" element={<ProtectedRoute><RulesPage /></ProtectedRoute>} />
            <Route path="/inbox-zero" element={<ProtectedRoute><InboxZeroPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/ai-replies" element={<ProtectedRoute><AiRepliesPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="bottom-right"
            toastOptions={{
              classNames: {
                toast: 'bg-popover text-popover-foreground border',
              },
            }}
          />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
